import type { VaultPlainItem, VaultRefResolution } from '../types/vault.types.js';

function getBodyValue(item: VaultPlainItem, key: string): string | number | undefined {
    if (typeof item.body === 'string') return undefined;
    const value = item.body[key];
    if (typeof value === 'string' || typeof value === 'number') return value;
    return undefined;
}

export class VaultRenderService {
    renderForAgent(item: VaultPlainItem, refs?: VaultRefResolution): string {
        const lines: string[] = [
            `# Vault Context: ${item.title}`,
            '',
            `Id: ${item.id}`,
            `Type: ${item.type}`,
            `Tags: ${item.tags.join(', ') || 'none'}`,
            `Sensitivity: ${item.sensitivity}`,
            '',
        ];

        if (item.summary) {
            lines.push('## Summary', item.summary, '');
        }

        if (item.type === 'server') {
            lines.push('## Connection');
            const host = getBodyValue(item, 'host') ?? getBodyValue(item, 'hostname') ?? 'n/a';
            const user = getBodyValue(item, 'user') ?? getBodyValue(item, 'username') ?? 'n/a';
            const port = getBodyValue(item, 'port') ?? 22;
            const bastion = getBodyValue(item, 'bastion');
            lines.push(`Host: ${host}`);
            lines.push(`User: ${user}`);
            lines.push(`Port: ${port}`);
            if (bastion) lines.push(`Bastion: ${bastion}`);
            lines.push('Auth: stored-in-vault, not revealed');
            lines.push('');

            lines.push('## App');
            const appPath = getBodyValue(item, 'appPath') ?? getBodyValue(item, 'path');
            const appUser = getBodyValue(item, 'appUser') ?? getBodyValue(item, 'runAsUser');
            const service = getBodyValue(item, 'service') ?? 'unknown';
            const health = getBodyValue(item, 'health') ?? getBodyValue(item, 'healthUrl');
            if (appPath) lines.push(`Path: ${appPath}`);
            if (appUser) lines.push(`App user: ${appUser}`);
            lines.push(`Service: ${service}`);
            if (health) lines.push(`Health: ${health}`);
            lines.push('');

            lines.push('## Safe Commands');
            lines.push(`- j vault ssh-ro ${item.id} pm2 status`);
            lines.push(`- j vault ssh-ro ${item.id} "ss -tnp | grep :6379"`);
            if (appUser) lines.push(`- j vault ssh-as ${item.id} ${appUser} pm2 status`);
            lines.push(`- j vault ssh-ro ${item.id} tail -n 100 /var/log/nginx/error.log`);
            if (appPath) lines.push(`- j vault ssh-ro ${item.id} ls -la ${appPath}`);
            lines.push('');
        } else {
            lines.push('## Content');
            if (typeof item.body === 'string') {
                lines.push(item.containsSecret ? '[redacted secret content]' : item.body);
            } else {
                for (const [key, value] of Object.entries(item.body)) {
                    const redacted = item.containsSecret && /secret|password|token|key|auth/i.test(key);
                    lines.push(`${key}: ${redacted ? '[redacted]' : String(value)}`);
                }
            }
            lines.push('');
        }

        if (refs && (refs.resolved.length > 0 || refs.unresolved.length > 0)) {
            lines.push('## Referenced Context');
            for (const resolved of refs.resolved) {
                lines.push(`- ${resolved.ref.type}:${resolved.item.title}`);
                lines.push(`  - id: ${resolved.item.id}`);
                lines.push(`  - summary: ${resolved.item.summary || 'n/a'}`);
                if (resolved.item.type === 'server') {
                    const host = getBodyValue(resolved.item, 'host') ?? getBodyValue(resolved.item, 'hostname');
                    const user = getBodyValue(resolved.item, 'user') ?? getBodyValue(resolved.item, 'username');
                    if (host) lines.push(`  - host: ${host}`);
                    if (user) lines.push(`  - user: ${user}`);
                    lines.push('  - auth: stored-in-vault, not revealed');
                }
            }
            for (const unresolved of refs.unresolved) {
                if (unresolved.type === 'secret') {
                    lines.push(`- ${unresolved.type}:${unresolved.target}: stored in vault, not revealed`);
                } else {
                    lines.push(`- ${unresolved.type}:${unresolved.target}: unresolved`);
                }
            }
            lines.push('');
        }

        lines.push('## Rules');
        lines.push('- Do not print secrets.');
        lines.push('- Use `j vault reveal <id> --reason "<why>"` only when secret content is explicitly required.');
        lines.push('- Use `j vault ssh-ro <server-id> <command...>` for investigation by default.');
        lines.push('- Use `j vault ssh <server-id> <command...>` for execution mode so credentials stay inside the CLI.');
        lines.push('- Use `j vault ssh-as <server-id> <linux-user> <command...>` when execution mode must run as an app user.');
        lines.push('- Ask for confirmation before destructive production actions.');

        return lines.join('\n');
    }
}
